import Button from "../../UI/Button/Button";
import Cover from "../../UI/Cover/Cover";
import QuantityStepper from "../../UI/QuantityStepper/QuantityStepper";
import { ReactComponent as InfoIcon } from "../../assets/icons/info_circle.svg";
import "./CheckOutList.css";
import { ReactElement, useContext, useEffect, useMemo, useState } from "react";
import { Item, cartItem, extrasCartItem } from "../../state_management/reducers/cart";
import { coupon_error_message, currency } from "../../utilities/misc";
import { epics_cart_items } from "../../utilities/cartUtilities";
import { CouponObjectContext, LocaleContext } from "../../AppProvider";
import { c_item, e_item } from "../../state_management/epics/order";
import { orderInfo, orderType } from "../../state_management/reducers/order";
import { TimeSelectionWarningContext, ZeroItemsContext } from "../../pages/CheckOut/CheckOutProvider";

type commonProps = {
  coupon: string | null;
  buttonJSX: ReactElement;
  cartTotal: number;
  orderDetails: orderType;
  cartMerged: {
    cart: cartItem[];
    gift_values: cartItem[];
    extras: extrasCartItem[];
  };
  pageCount?: number;
  setPageCount?: (data: number) => void;
  setOrderDetails?: (data: Partial<orderType>) => void;
  setOrderInfo?: (data: orderInfo) => void;
  changeCartItem?: (data: Item) => void;
};
type readOnlySummary = {
  cartWithCouponTotal: number;
  couponCheck: boolean;
  couponLoading: boolean;
  deactivateBars: true;
};

type editableSummary = {
  cartWithCouponTotal: number;
  couponLoading?: never;
  couponCheck?: never;
  coupon: string | null;
  deactivateBars: false;
};

interface DisplaySummaryItem {
  id: number;
  name: string;
  photo_path: string | null;
  price: number;
  price_with_discount: number;
  discount?: number | null;
  duration: number;
  quantity: number;
  type: "typical" | "extra" | "gift";
  parent_id?: number;
  rawItem: Item;
}

const getItemKey = (item: {
  type: string;
  id: number | string;
  parent_id?: number | string | null;
}) => `${item.type}-${item.id}-${item.parent_id || 0}`;

const CheckOutSummary = ({
  cartMerged,
  coupon,
  cartTotal,
  buttonJSX,
  deactivateBars,
  cartWithCouponTotal,
  couponLoading,
  orderDetails,
  couponCheck,
  pageCount,
  setPageCount,
  setOrderDetails,
  setOrderInfo,
  changeCartItem
}: commonProps & (readOnlySummary | editableSummary)) => {
  const { cart, extras, gift_values } = cartMerged;
  const [showTimeSelectionWarning, setShowTimeSelectionWarning] = useContext(TimeSelectionWarningContext);

  const cartCombined = useMemo((): DisplaySummaryItem[] => {
    const mergeCart = (items: cartItem[]): DisplaySummaryItem[] => {
      return items.map((cartEntry) => {
        const service = cartEntry.item;
        return {
          discount: service.discount,
          duration: service.duration,
          id: service.id,
          name: service.name,
          photo_path: service.photo_path,
          price: service.price,
          price_with_discount: service.price_with_discount,
          quantity: cartEntry.quantity,
          type: cartEntry.type,
          parent_id: undefined,
          rawItem: cartEntry as Item
        };
      });
    };

    const mergeExtras = (items: extrasCartItem[]): DisplaySummaryItem[] => {
      return items.map((extraEntry) => {
        const service = extraEntry.item;
        return {
          discount: service.discount,
          duration: service.duration,
          id: service.id,
          name: service.name,
          photo_path: service.photo_path,
          price: service.price,
          price_with_discount: service.price_with_discount,
          quantity: extraEntry.quantity,
          type: "extra" as const,
          parent_id: extraEntry.parent_id,
          rawItem: extraEntry as Item
        };
      });
    };

    return [...mergeCart(cart), ...mergeCart(gift_values), ...mergeExtras(extras)];
  }, [cartMerged]);

  const deduplicatedCart = useMemo((): DisplaySummaryItem[] => {
    const itemMap = new Map<string, DisplaySummaryItem>();
    for (const item of cartCombined) {
      const key = getItemKey(item);
      const existing = itemMap.get(key);
      if (existing) {
        itemMap.set(key, {
          ...existing,
          quantity: existing.quantity + item.quantity
        });
      } else {
        itemMap.set(key, { ...item });
      }
    }
    return Array.from(itemMap.values());
  }, [cartCombined]);

  const {
    zeroItemsMap,
    setZeroItem,
    removeZeroItem,
    itemOrder,
    registerItemKeys
  } = useContext(ZeroItemsContext);

  const isLastStep = pageCount !== undefined && pageCount >= 4;
  const isEditable = !deactivateBars && !isLastStep;

  useEffect(() => {
    registerItemKeys(deduplicatedCart.map((i) => getItemKey(i)));
  }, [deduplicatedCart, registerItemKeys]);

  const allDisplayItems = useMemo((): DisplaySummaryItem[] => {
    if (!isEditable) {
      return deduplicatedCart;
    }

    const activeMap = new Map<string, DisplaySummaryItem>();
    for (const item of deduplicatedCart) {
      activeMap.set(getItemKey(item), item);
    }

    const items: DisplaySummaryItem[] = [];
    const seen = new Set<string>();

    for (const key of itemOrder) {
      seen.add(key);
      if (zeroItemsMap[key]) {
        items.push(zeroItemsMap[key] as DisplaySummaryItem);
      } else if (activeMap.has(key)) {
        items.push(activeMap.get(key)!);
      }
    }

    for (const item of deduplicatedCart) {
      const key = getItemKey(item);
      if (!seen.has(key)) {
        seen.add(key);
        if (zeroItemsMap[key]) {
          items.push(zeroItemsMap[key] as DisplaySummaryItem);
        } else {
          items.push(item);
        }
      }
    }

    return items;
  }, [itemOrder, zeroItemsMap, deduplicatedCart, isEditable]);

  const { t, locale } = useContext(LocaleContext);
  const { CouponObject } = useContext(CouponObjectContext);

  const cartMainTotal = useMemo(() => {
    let total = 0;
    for (const item of allDisplayItems) {
      if (item.quantity > 0) {
        total = total + item.quantity * item.price;
      }
    }
    return total;
  }, [allDisplayItems]);

  const { couponRes } = CouponObject;
  const coupon_loading = !couponCheck ? couponLoading : couponRes.loading;

  const getMinQuantity = (serviceItem: DisplaySummaryItem): number => {
    if (serviceItem.quantity === 0) return 0;
    if (serviceItem.type === "extra") return 0;
    if (serviceItem.type === "typical") {
      const activeTypicalCount = allDisplayItems.filter(
        (i) => i.type === "typical" && i.quantity > 0
      ).length;
      return activeTypicalCount > 1 ? 0 : 1;
    }
    return 1;
  };

  const handleQuantityChange = (
    serviceItem: DisplaySummaryItem,
    newQuantity: number
  ) => {
    if (!isEditable || !changeCartItem) return;

    const key = getItemKey(serviceItem);

    if (newQuantity === 0) {
      setZeroItem(key, { ...serviceItem, quantity: 0 });
      changeCartItem({
        ...serviceItem.rawItem,
        id: serviceItem.id,
        type: serviceItem.type,
        quantity: 0,
        ...(serviceItem.parent_id !== undefined ? { parent_id: serviceItem.parent_id } : {})
      } as Item);
    } else {
      removeZeroItem(key);
      changeCartItem({
        ...serviceItem.rawItem,
        id: serviceItem.id,
        type: serviceItem.type,
        quantity: newQuantity,
        ...(serviceItem.parent_id !== undefined ? { parent_id: serviceItem.parent_id } : {})
      } as Item);
    }

    if (pageCount !== undefined && pageCount > 1) {
      setOrderDetails?.({ time_start: null });
      setOrderInfo?.(null);
      setPageCount?.(1);
      setShowTimeSelectionWarning(true);
    } else if (pageCount === 1) {
      if (orderDetails.time_start) {
        setOrderDetails?.({ time_start: null });
      }
      setShowTimeSelectionWarning(true);
    }
  };

  return (
    <>
      <div className="check_list_wrap">
        {allDisplayItems.map((service) => {
          const isZero = service.quantity === 0;
          return (
            <div
              key={getItemKey(service)}
              className={`check_list_item flex ${isZero ? "zero_quantity" : ""}`}
            >
              <div className="img">
                {!isEditable && (
                  <div className="checklist_float_indicator indicator">
                    {service.quantity}
                  </div>
                )}
                <Cover className="">
                  <img src={service.photo_path || ``} alt="" />
                </Cover>
              </div>
              <div className="title flex1 med_text">{service.name}</div>
              {isEditable && (
                <div className="check_list_item_stepper">
                  <QuantityStepper
                    value={service.quantity}
                    min={getMinQuantity(service)}
                    disabled={deactivateBars}
                    onChange={(newQty) => handleQuantityChange(service, newQty)}
                  />
                </div>
              )}
              <div className="amount med_text">
                {!isZero && service.discount ? (
                  <div className="strike_through">
                    {currency(
                      (service.quantity * service.price).toFixed(2),
                      locale
                    )}
                  </div>
                ) : null}
                {!isZero && (
                  <div className="fontBlue">
                    {currency(
                      (service.quantity * service.price_with_discount).toFixed(2),
                      locale,
                      service.type === "gift"
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {isEditable && showTimeSelectionWarning && (
          <div className="checkout_qty_warning_banner">
            <div className="warning_icon">
              <InfoIcon className="img_div_contain" />
            </div>
            <div className="warning_text">
              {t("Because you changed service quantities, please choose date & time again.")}
            </div>
          </div>
        )}
        {!couponCheck && deactivateBars ? (
          ``
        ) : (
          <CouponForm
            cart={cart}
            extras={extras}
            gift_values={gift_values}
            couponCheck={couponCheck}
            orderDetails={orderDetails}
            deactivateBars={deactivateBars}
          />
        )}
      </div>
      <div className="check_list_total">
        <div className="subTotal med_text">
          <div>
            <div className="med_text">{t("Subtotal")}</div>
            <div className="med_text">
              {currency(cartMainTotal.toFixed(2), locale)}
            </div>
          </div>
          <div className="fontBlue">
            <div className="med_text">{t("You save")}</div>
            <div className="med_text">
              - {currency((cartMainTotal - cartTotal).toFixed(2), locale)}
            </div>
          </div>
          {coupon && (
            <div className="fontBlue">
              <div className="med_text">{t("Coupon")}</div>
              <div className="med_text">
                {coupon_loading
                  ? "..."
                  : `- ${currency(
                      Math.abs(cartTotal - cartWithCouponTotal).toFixed(2),
                      locale
                    )}`}
              </div>
            </div>
          )}
        </div>
        <div className="finalTotal">
          <div>
            <div>{t("Total")}</div>
            <div>
              {coupon_loading
                ? "..."
                : currency(cartWithCouponTotal.toFixed(2), locale)}
            </div>
          </div>
        </div>
      </div>
      {buttonJSX}
    </>
  );
};

export default CheckOutSummary;

export const CouponForm = ({
  cart,
  extras,
  gift_values,
  couponCheck,
  deactivateBars,
  orderDetails,
  isSmall
}: {
  cart: c_item[];
  extras: e_item[];
  gift_values: (c_item & {
    item: {
      price: number;
    };
  })[];
  orderDetails: orderType;
  couponCheck: boolean | undefined;
  deactivateBars: boolean;
  isSmall?: boolean;
}) => {
  const { CouponObject } = useContext(CouponObjectContext);
  const {
    couponRes,
    localCouponValue,
    setShowDiscount,
    showDiscount,
    couponError,
    couponErrorType,
    setLocalCouponValue,
    disabledCouponInput,
    isApplied
  } = CouponObject;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    couponRes.execute({
      coupon_code: localCouponValue,
      details: orderDetails,
      show_banner: false,
      ...epics_cart_items({
        cart,
        extras,
        gift_values
      })
    });
  };

  const { t } = useContext(LocaleContext);

  return !couponCheck && deactivateBars ? (
    <></>
  ) : (
    <div className={`coupon_form_wrapper ${isSmall ? "showSm" : "showLg"}`}>
      <span
        onClick={() => setShowDiscount(!showDiscount)}
        className="coupon_form_span_txt fontBlue med_text"
      >
        {t("Have a discount code?")}
      </span>
      {disabledCouponInput || showDiscount ? (
        <>
          <form
            method="POST"
            onSubmit={handleSubmit}
            className="check_out_discount_code_wrapper flex"
          >
            <input
              type="text"
              disabled={disabledCouponInput}
              readOnly={disabledCouponInput}
              value={localCouponValue || ``}
              onChange={(e) => {
                const value = e.target.value;
                setLocalCouponValue(value);
              }}
            />
            <Button
              type="submit"
              disabled={disabledCouponInput || couponRes.loading}
              size="sm"
            >
              {t("Apply")}
            </Button>
          </form>
          <div
            className={`${
              isApplied ? "fontBlue" : "errorMsg"
            } coupon_info_text`}
          >
            {isApplied
              ? t("Applied successfully")
              : couponError && showDiscount && localCouponValue
              ? coupon_error_message(couponErrorType, t)
              : ""}
          </div>
        </>
      ) : (
        <></>
      )}
    </div>
  );
};
